import { useEffect, useMemo, useRef, useState } from 'react';
import { useOptionalLifeOS } from './LifeOSProvider';
import { lifeOSItemTitle, lifeOSTime } from './presentation';

const DISMISS_PREFIX = 'athena.life-os.context-card.v1';

function readDismissed(ownerId) {
  if (typeof window === 'undefined' || !ownerId) return {};
  try {
    const parsed = JSON.parse(window.localStorage.getItem(`${DISMISS_PREFIX}:${ownerId}`) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeDismissed(ownerId, values) {
  if (typeof window === 'undefined' || !ownerId) return;
  try { window.localStorage.setItem(`${DISMISS_PREFIX}:${ownerId}`, JSON.stringify(values)); } catch { /* best effort */ }
}

function tomorrowIso() {
  return new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
}

function isExpired(card) {
  return Boolean(card?.expires_at) && Number.isFinite(Date.parse(card.expires_at)) && Date.parse(card.expires_at) <= Date.now();
}

/**
 * Home intentionally gets at most two temporary prompts, derived from the
 * shared Today state. The full agenda remains in Today; this is only a
 * relevance-aware doorway back to it.
 */
export function HomeContextCards({ ownerId, onOpenToday }) {
  const lifeOS = useOptionalLifeOS();
  const [dismissed, setDismissed] = useState(() => readDismissed(ownerId));
  const shownCardsRef = useRef(new Set());
  const today = lifeOS?.today?.data;

  useEffect(() => {
    setDismissed(readDismissed(ownerId));
  }, [ownerId]);

  useEffect(() => {
    if (!lifeOS?.today?.data && !lifeOS?.today?.loading) void lifeOS?.loadToday();
  }, [lifeOS]);

  const cards = useMemo(() => {
    if (!today) return [];
    const result = [];
    const next = today.next_item;
    if (next?.id && !['past', 'completed', 'skipped', 'cancelled'].includes(String(next.status || '').toLowerCase())) {
      result.push({
        id: `next:${next.id}:${today.date || ''}`,
        title: 'Próximo compromisso',
        detail: `${lifeOSItemTitle(next)}${next.time ? ` · ${lifeOSTime(next.time, today.timezone)}` : ''}`,
        reason: 'Próximo item da sua timeline',
        priority: next.priority || 'normal',
        expires_at: next.end_time || next.time || tomorrowIso(),
        dismissed: false,
        source: 'timeline',
      });
    }
    const open = [...(today.to_place || []), ...(today.timeline || [])]
      .filter(item => ['overdue', 'pending', 'in_progress'].includes(String(item?.status || '').toLowerCase()))
      .filter(item => ['TASK', 'OPEN_LOOP', 'PROJECT_DEADLINE'].includes(String(item?.type || '').toUpperCase()))
      .sort((left, right) => {
        const rank = { urgent: 0, high: 1, normal: 2, low: 3 };
        return (rank[left?.priority] ?? 2) - (rank[right?.priority] ?? 2);
      })[0];
    if (open?.id) {
      result.push({
        id: `open:${open.id}:${today.date || ''}`,
        title: open.status === 'overdue' ? 'Pendência vencida' : 'Pendência relevante',
        detail: lifeOSItemTitle(open),
        reason: open.status === 'overdue' ? 'O prazo já passou' : 'Item sem destino definido',
        priority: open.priority || (open.status === 'overdue' ? 'high' : 'normal'),
        expires_at: tomorrowIso(),
        dismissed: false,
        source: String(open.source || 'today'),
      });
    }
    return result.slice(0, 2);
  }, [today]);

  const visible = cards.filter(card => !dismissed[card.id] && !isExpired(card));

  useEffect(() => {
    if (!lifeOS?.trackTechnicalEvent) return;
    for (const card of visible) {
      if (shownCardsRef.current.has(card.id)) continue;
      shownCardsRef.current.add(card.id);
      void lifeOS.trackTechnicalEvent('context_card_shown', 'home');
    }
  }, [lifeOS, visible]);

  if (!visible.length) return null;

  function dismiss(card) {
    const next = { ...dismissed, [card.id]: new Date().toISOString() };
    setDismissed(next);
    writeDismissed(ownerId, next);
    void lifeOS?.trackTechnicalEvent?.('context_card_dismissed', 'home');
  }

  return (
    <aside
      aria-label="Contexto relevante da Athena"
      style={{ position: 'absolute', top: 14, left: '50%', zIndex: 11, width: 'min(440px, calc(100vw - 32px))', transform: 'translateX(-50%)', display: 'grid', gap: 7 }}
    >
      {visible.map(card => (
        <article key={card.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'center', padding: '10px 11px 10px 13px', borderRadius: 14, border: '1px solid rgba(105,199,255,.18)', background: 'rgba(5,19,42,.72)', boxShadow: '0 10px 30px rgba(0,0,0,.18)', backdropFilter: 'blur(12px)' }}>
          <button type="button" onClick={onOpenToday} style={{ minHeight: 42, padding: 0, border: 0, background: 'transparent', color: 'inherit', textAlign: 'left', cursor: 'pointer' }} aria-label={`${card.title}: ${card.detail}. Abrir Hoje.`}>
            <span style={{ display: 'block', color: 'rgba(214,241,255,.94)', fontSize: 12, fontWeight: 650 }}>{card.title}</span>
            <span style={{ display: 'block', marginTop: 2, color: 'rgba(204,229,248,.68)', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{card.detail}</span>
            <span style={{ display: 'block', marginTop: 2, color: 'rgba(145,194,222,.58)', fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{card.reason}</span>
          </button>
          <button type="button" onClick={() => dismiss(card)} aria-label={`Dispensar: ${card.title}`} title="Dispensar" style={{ width: 44, minHeight: 44, border: 0, borderRadius: 10, background: 'rgba(164,211,255,.08)', color: 'rgba(220,241,255,.76)', cursor: 'pointer', fontSize: 18 }}>×</button>
        </article>
      ))}
    </aside>
  );
}

export default HomeContextCards;
